import {
  Box, Container, Typography,
} from '@material-ui/core';
import React, {
  useState,
  useMemo,
  useContext,
  useEffect,
} from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ReactGA from 'react-ga';
import jwtDecode from 'jwt-decode';

import useStyles from './useStyles';

import { StepperContext } from '../../../../context/stepper';
import fields from '../../../../data/inputLoginAgent.json';
import Input from '../../../../components/Input';
import { AlertDialog, Button, Loader } from '../../../../components';
import {
  storeAuth, storeStepData, getLogoAndImageTemplate,
  getTokenAndRedirection,
  logOutMain,
  AuthError,
  saveExtraDataAuth,
} from '../../../../utils';
import * as authAPI from '../../../../api/auth';
import * as userAPI from '../../../../api/users';
import {
  getFlowTypeHandler, getStepperTags,
} from '../../../../utils/tags';
import useLoading from '../../../../hooks/useLoading';
import { AuthContext } from '../../../../context/auth';
import ErrorMessage from '../../../../components/ErrorMessage';
import AlertSnack from '../../../../components/AlertSnack';
import useError from '../../../../hooks/useError';
import {
  AGENTE_CLIENTS_ROUTE, AGENTE_REGISTRO_ROUTE,
  COMPANY, LOGIN_PHONE_ID,
} from '../../../../constants';
import { ThemeContext } from '../../../../context/Theme/themeContext';
import { useProduct } from '../../../../hooks/useProduct';
import { InformationDialog } from '../../../../components/InformationDialog';
import { SendCodeDialog } from '../../../../components/SendCodeDialog';
import { getConfigs } from '../../../../utils/configs';
import { IMAGE_HEADER } from '../../../../constants/commonUrls';

const INITIAL_ERROR = {
  isError: false,
  errorMessage: 'La verificacionestamal',
};
export const LoginAgent = () => {
  const { initTimeoutToken } = useContext(AuthContext);
  const { initStepper } = useContext(StepperContext);
  const { loadTheme } = useContext(ThemeContext);
  const { products } = useProduct();
  const classes = useStyles();
  const { isLoading, startLoading, endLoading } = useLoading();
  const {
    isError,
    errorType,
    errorMessage,
    showError,
    hideError,
  } = useError();
  const [searchParams] = useSearchParams();
  const { product = '' } = useParams();

  const {
    logoUrl,
  } = useMemo(() => getLogoAndImageTemplate(product), []);

  const [formFields, setFormFields] = useState(fields);
  const [phoneValue, setPhoneValue] = useState('');
  const [idRoles, setIdRoles] = useState(['']);

  const [showModal, setShowModal] = useState(false);
  const [isErrorPhoneValue, setIsErrorPhoneValue] = useState(INITIAL_ERROR);
  const [showSendCodeDialog, setShowSendCodeDialog] = useState(false);
  const [showInformationDialog, setShowInformationDialog] = useState(false);
  const disable = useMemo(() => formFields.some((field) => !field.value), [formFields]);
  const navigate = useNavigate();

  const handleChange = (event, value) => {
    const { name } = event.target;
    // eslint-disable-next-line max-len
    const newFields = formFields.map((field) => (field.name === name ? { ...field, value } : field));
    setFormFields(newFields);
  };
  // jose+t02250823@docupass.co
  // T02250823

  const loginToken = async (token) => {
    const { validator: { email = '' } = {} } = jwtDecode(token);
    const { user } = await authAPI.loginByToken(email, token);
    storeAuth(user);
    return user;
  };

  const getActualStep = (stepperTags) => {
    const actualIndexStep = stepperTags.findIndex(({ completed }) => !completed);
    return { stepRoute: stepperTags[actualIndexStep].route, currentStep: actualIndexStep };
  };

  const setTags = async (process, normalizedFlowName) => {
    const stepperInitialization = await getStepperTags(normalizedFlowName, process);
    initStepper(stepperInitialization);
    const { steps } = stepperInitialization;
    const actualStep = getActualStep(steps);
    initStepper({ ...stepperInitialization, current: actualStep.currentStep });
    storeStepData({ ...actualStep, steps });
    return actualStep;
  };

  const onLogout = async () => {
    await logOutMain();
    await authAPI.logout();
  };

  const backPhone = () => {
    setShowSendCodeDialog(false);
  };

  const generateCode = async (user, phone) => {
    const { whatsappSended } = await userAPI.generatePhoneCode(user);
    setPhoneValue(phone);
    setShowSendCodeDialog(true);
    return whatsappSended;
  };

  const onSubmit = async (code) => {
    try {
      await onLogout();
      startLoading();

      const phone = formFields[0].value;
      const userObject = {
        phone: `+521${phone}`,
        code,
        model: 'agentAssigned',
        modelId: COMPANY,
        roles: idRoles,
      };
      const { user } = await userAPI.loginByPhoneCode(userObject);
      storeAuth(user);
      endLoading();
      initTimeoutToken();
      navigate(AGENTE_CLIENTS_ROUTE);
    } catch (e) {
      showError(e.message, e.name || 'Error trying to submit');
      endLoading();
    }
  };

  const loginByTokenFlow = async () => {
    try {
      const queryParams = getTokenAndRedirection(searchParams);
      if (!queryParams) return false;
      const { token, redirection, process } = queryParams;
      startLoading();
      await loginToken(token);
      const { normalizedFlowName } = await getFlowTypeHandler(process);
      await setTags(process, normalizedFlowName);
      saveExtraDataAuth({ process });
      await loadTheme(products[0]);
      initTimeoutToken();
      return navigate(redirection);
    } catch (e) {
      showError(e.message, e.name || 'Error trying to loginByTokenFlow');
      return false;
    }
  };

  const verifyCode = async (event, code) => {
    try {
      // validacion del  codigo
      event.preventDefault();
      startLoading();
      setShowSendCodeDialog(false);
      const phone = formFields[0].value;
      const correctPhone = `+521${phone}`;
      const { verified } = await userAPI.verifyPhoneCode(correctPhone, code);
      if (verified) {
        await onSubmit(code);
      } else {
        setShowSendCodeDialog(true);
        setIsErrorPhoneValue({
          isError: true,
          errorMessage: 'Error al verificar el numero',
        });
      }
      endLoading();
    } catch (e) {
      console.log({ e });
      showError(e.message, e.name || 'Error trying to submit');
      endLoading();
      if (e instanceof AuthError) setShowModal(true);
    }
  };

  const checkPhoneAlreadyExists = async (user) => {
    const existsResponse = await userAPI.verifyPhoneAlreadyExists(user);
    const { exists = false } = existsResponse;
    return exists;
  };

  const sendCode = async (event) => {
    event.preventDefault();
    startLoading();
    const phone = formFields[0].value;
    const userObject = {
      company: COMPANY,
      phone: `+521${phone}`,
      shouldCheckCompanyConfig: false,
    };

    const exists = await checkPhoneAlreadyExists(userObject);
    if (!exists) {
      setShowModal(true);
      endLoading();
      return;
    }
    await generateCode(userObject, phone);
    endLoading();
  };

  const fetchAgentRolesId = async () => {
    const payloadConfig = await getConfigs(LOGIN_PHONE_ID);
    const { agentRoles } = payloadConfig;
    setIdRoles(agentRoles);
  };

  const fetchData = async () => {
    try {
      startLoading();
      await fetchAgentRolesId();
      endLoading();
    } catch (e) {
      showError(e.message, e.name || 'Error trying to fetch  Data');
      endLoading();
    }
  };

  useEffect(() => {
    loginByTokenFlow();
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    ReactGA.pageview(window.location.pathname + window.location.search);
  }, []);

  return (
    <>
      <AlertSnack
        isOpen={isError}
        type="error"
        onClose={hideError}
      >
        <ErrorMessage
          errorType={errorType}
          errorMessage={errorMessage}
        />
      </AlertSnack>
      <AlertDialog
        onCancel={() => setShowModal(false)}
        onAccept={() => navigate(AGENTE_REGISTRO_ROUTE)}
        title="Intentas acceder con un teléfono que no está registrado"
        showModal={showModal}
        onCancelText="Acceder con otro teléfono"
        onAcceptText="Registrarme"
      />
      <SendCodeDialog
        onCancel={() => setShowSendCodeDialog(false)}
        backPhone={backPhone}
        onSubmit={verifyCode}
        phone={phoneValue}
        showModal={showSendCodeDialog}
        error={isErrorPhoneValue}
        title="Verifica tu celular"
        useEditPhone
        buttonText="Continuar"
      />
      <InformationDialog
        showModal={showInformationDialog}
        onSubmit={() => setShowInformationDialog(false)}
        buttonTitle="Ingresar a Chiplo"
        descripcion="El celular que estás ingresando con nosotros no tiene una cuenta asociada. Por favor ingresa a Chiplo.com y crea un proceso nuevo."
      />
      <Loader open={isLoading} />
      <Box
        component="form"
        className={classes.formContainer}
        onSubmit={sendCode}
        method="post"
        autoComplete="off"
      >
        <Box>
          <Box className={classes.boxImage}>
            <img
              className={classes.imgLogo}
              src={logoUrl}
              alt="imgLogoMega"
            />
          </Box>
          <Box className={classes.imageContainer}>
            <img
              className={classes.image}
              src={IMAGE_HEADER}
              alt="imgLogoMega"
            />
          </Box>
        </Box>
        <Container
          component="div"
          maxWidth={false}
          className={classes.containerTitle}
        >
          <Typography className={classes.title}>
            Agente Inmobiliario: Ingresa con tu celular para continuar
          </Typography>
        </Container>
        <Box className={classes.inputContainer}>
          {formFields.map((field) => (
            <Box className={classes.inputFieldContainer}>
              <Typography
                variant="h6"
                className={classes.labelInput}
              >
                {field.title}
              </Typography>
              <Input
                label={field.label}
                type={field.fieldType?.name}
                name={field.name}
                value={field.value || ''}
                required={Boolean(field?.config?.required)}
                disabled={field?.config?.disabled}
                hide={Boolean(field?.config?.hide)}
                isEmpty={field?.isEmpty}
                errorMessage={field?.errorMessage}
                defaultValue={field?.config?.defaultValue}
                onChange={handleChange}
                extraInputClassName={classes.customPasswordInput}
              />
            </Box>
          ))}
        </Box>
        <Box className={classes.buttonContainer}>
          <Button
            disabled={disable}
            type="submit"
            className={classes.btnLogin}
            fullWidth
            classes={{
              outlined: classes.btnRoot,
            }}
          >
            Enviar código
          </Button>
        </Box>
      </Box>
      <Box />
    </>
  );
};
